import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Statik sayfa yolları → başlık etiketi. Dinamik sayfalar (aşağıda) ayrı ele alınır.
const ROUTE_TITLES = {
    '/filmler': 'Filmler',
    '/diziler': 'Diziler',
    '/canli-tv': 'Canlı TV',
    '/paketler': 'Paketler',
    '/yardim': 'Yardım Merkezi',
    '/giris-yap': 'Giriş Yap',
    '/uye-ol': 'Üye Ol',
    '/listem': 'Listem',
    '/tv': 'Televizyonu Bağla',
};

// Uygulamada tanımlı geçerli rotalar. Bunların hiçbirine uymayan yol = 404 (soft 404).
// Google'ın 404 ekranını "404" başlığıyla indekslemesini engellemek için ayrıca kullanılır.
const STATIC_ROUTES = new Set([
    '/', '/giris-yap', '/sifremi-unuttum', '/sifre-sifirla', '/uye-ol',
    '/paketler', '/canli-tv', '/filmler', '/diziler', '/yardim',
    '/odeme', '/profil', '/profiller', '/profil-sec', '/listem', '/admin', '/tv',
]);
const DYNAMIC_ROUTES = [
    /^\/detay\/[^/]+$/,
    /^\/izle\/[^/]+$/,
    /^\/kesfet\/[^/]+$/,
    /^\/yasal\/[^/]+$/,
];
function isKnownRoute(pathname) {
    if (STATIC_ROUTES.has(pathname)) return true;
    // Tanımsız yasal slug SPA'da "Belge bulunamadı" gösterir → 404 sayılmalı
    const legal = pathname.match(/^\/yasal\/([^/]+)$/);
    if (legal) return Boolean(LEGAL_TITLES[legal[1]]);
    return DYNAMIC_ROUTES.some((re) => re.test(pathname));
}

// ── Sunucu tarafıyla (backend/routes/pageMeta.js) EŞ TUTULMASI GEREKEN KURALLAR ──
// Googlebot sayfayı JS ile render eder; burada verilen değer sunucunun bastığını
// EZER. İkisi çelişirse sunucudaki karar hiçbir işe yaramaz. backend/scripts/seoCheck.js
// iki dosyanın bu üç kuralda ayrışmadığını denetler.

// /kesfet/:genre dizine girmez (gerekçe pageMeta.js'te). Tür sayfalarını yeniden
// dizine sokmak isterseniz burayı VE pageMeta.js'teki eşini true yapın.
const KESFET_INDEXABLE = false;

// LegalPage.js'teki LEGAL_DOCS anahtarları ve başlıkları (pageMeta.js ile aynı olmalı)
const LEGAL_TITLES = {
    gizlilik: 'Gizlilik Politikası',
    kvkk: 'KVKK Aydınlatma Metni',
    aydinlatma: 'Çerez Politikası',
    terms: 'Kullanım Şartları ve Üyelik Sözleşmesi',
    onbilgilendirme: 'Ön Bilgilendirme Koşulları',
    mesafeli: 'Mesafeli Satış Sözleşmesi',
    withdrawal: 'Cayma Hakkı ve Abonelik İptal Koşulları',
    'hesap-silme': 'Hesap ve Veri Silme',
};

// /izle/:id → /detay/:id. Aynı içeriğin iki URL'i dizine girmesin; asıl sayfa detaydır.
function canonicalPathFor(pathname) {
    const w = pathname.match(/^\/izle\/([^/]+)$/);
    return w ? `/detay/${w[1]}` : pathname;
}

// pageMeta.js'teki NOINDEX_PREFIXES ile aynı liste
const NOINDEX_PREFIXES = [
    '/admin', '/profil', '/profiller', '/profil-sec', '/odeme', '/listem',
    '/giris-yap', '/uye-ol', '/sifremi-unuttum', '/sifre-sifirla', '/tv',
];

// Sayfa dizine girmemeli mi? (kişisel sayfalar zaten robots.txt ile de kapalı)
function isNoindexRoute(pathname) {
    if (!KESFET_INDEXABLE && /^\/kesfet\/[^/]+$/.test(pathname)) return true;
    const legal = pathname.match(/^\/yasal\/([^/]+)$/);
    if (legal && !LEGAL_TITLES[legal[1]]) return true;
    return NOINDEX_PREFIXES.some((pre) => pathname === pre || pathname.startsWith(pre + '/'));
}

// Global SEO ayarları bir kez çekilip önbelleğe alınır (her rota değişiminde tekrar çekilmez).
let settingsPromise = null;
function loadSeoSettings() {
    if (!settingsPromise) {
        settingsPromise = fetch(`${BASE_URL}/api/settings`)
            .then((r) => r.json())
            .then((d) => (d?.success ? { ...(d.settings?.seo || {}), pageSeo: d.settings?.pageSeo || {} } : {}))
            .catch(() => ({}));
    }
    return settingsPromise;
}

function upsertMeta(attr, key, content) {
    const sel = `meta[${attr}="${key}"]`;
    if (content == null || content === '') {
        const existing = document.head.querySelector(sel);
        if (existing && existing.dataset.seo) existing.remove();
        return;
    }
    let el = document.head.querySelector(sel);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.dataset.seo = '1';
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function upsertLink(rel, href) {
    // data-seo şartı YOK: sunucunun bastığı canonical de bulunup güncellenmeli,
    // yoksa ikinci bir canonical eklenir (Google ikisini de yok sayabilir).
    const sel = `link[rel="${rel}"]`;
    if (!href) {
        const existing = document.head.querySelector(sel);
        if (existing) existing.remove();
        return;
    }
    let el = document.head.querySelector(sel);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.dataset.seo = '1';
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

// Başlık şablonunu uygula. Anasayfada ham başlık, alt sayfalarda "%s | Site" kalıbı.
function buildTitle(seo, pageLabel) {
    const fallback = seo.defaultTitle || 'FİLBOX';
    if (!pageLabel) return fallback;
    const tpl = seo.titleTemplate;
    if (tpl && tpl.includes('%s')) return tpl.replace('%s', pageLabel);
    return `${pageLabel} | ${seo.siteName || 'FİLBOX'}`;
}

// Verilen rotaya göre sayfaya özel { label, description, image } döndürür.
// Detay/izle sayfalarında içeriği API'den çeker.
async function resolvePage(pathname) {
    // /detay/:id veya /izle/:id → içerik meta'sı
    const m = pathname.match(/^\/(?:detay|izle)\/([^/]+)$/);
    if (m) {
        try {
            const r = await fetch(`${BASE_URL}/api/content/${m[1]}`);
            const d = await r.json();
            if (d?.success && d.content) {
                return {
                    label: d.content.title,
                    description: d.content.description || null,
                    image: d.content.backdrop || d.content.image || null,
                };
            }
        } catch { /* yut */ }
        return { label: null };
    }
    // /kesfet/:genre → tür adı + Keşfet
    const g = pathname.match(/^\/kesfet\/([^/]+)$/);
    if (g) {
        const genre = decodeURIComponent(g[1]);
        const nice = genre.charAt(0).toUpperCase() + genre.slice(1);
        return { label: `${nice} — Keşfet` };
    }
    // /yasal/:slug → belgenin kendi başlığı (pageMeta.js ile aynı liste)
    const legal = pathname.match(/^\/yasal\/([^/]+)$/);
    if (legal) return { label: LEGAL_TITLES[legal[1]] || null };
    // Statik yollar
    if (ROUTE_TITLES[pathname]) return { label: ROUTE_TITLES[pathname] };
    // Anasayfa ve diğerleri → ham varsayılan başlık
    return { label: null };
}

// Admin panelden yönetilen SEO ayarlarını çalışma anında <head>'e uygular.
export default function SeoHead() {
    const location = useLocation();

    useEffect(() => {
        let cancelled = false;

        Promise.all([loadSeoSettings(), resolvePage(location.pathname)]).then(([seo, page]) => {
            if (cancelled) return;

            // Tanımlı hiçbir rotaya uymayan yol → 404 sayfası. Google'ın soft 404'ü
            // "404" başlığıyla indekslememesi için noindex verilir ve başlık düzeltilir.
            const notFound = !isKnownRoute(location.pathname);

            // Sabit/kategori sayfaları için admin'in girdiği override (varsa) önceliklidir.
            const override = (seo.pageSeo && seo.pageSeo[location.pathname]) || {};
            const title = notFound
                ? buildTitle(seo, 'Sayfa Bulunamadı')
                : (override.title || buildTitle(seo, page.label));
            document.title = title;

            const description = override.description || page.description || seo.description;
            const image = override.ogImage || page.image || seo.ogImage;
            const canonicalPath = canonicalPathFor(location.pathname);
            const canonical = seo.canonicalBase
                ? seo.canonicalBase.replace(/\/$/, '') + canonicalPath
                : window.location.origin + canonicalPath;
            const noindex = notFound || seo.robotsIndex === false || isNoindexRoute(location.pathname);

            upsertMeta('name', 'description', description);
            upsertMeta('name', 'keywords', seo.keywords);
            upsertMeta('name', 'theme-color', seo.themeColor);
            // notFound → nofollow (takip edilecek bir şey yok); kural gereği noindex olan
            // sayfalarda follow KALIR: /kesfet üzerinden detay sayfaları keşfedilsin.
            upsertMeta('name', 'robots',
                notFound ? 'noindex, nofollow' : (noindex ? 'noindex, follow' : 'index, follow'));
            upsertMeta('name', 'google-site-verification', seo.googleVerification);

            // Open Graph
            upsertMeta('property', 'og:type', 'website');
            upsertMeta('property', 'og:site_name', seo.siteName);
            upsertMeta('property', 'og:title', title);
            upsertMeta('property', 'og:description', description);
            upsertMeta('property', 'og:image', image);
            upsertMeta('property', 'og:url', canonical);

            // Twitter Card
            upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
            upsertMeta('name', 'twitter:site', seo.twitterHandle);
            upsertMeta('name', 'twitter:title', title);
            upsertMeta('name', 'twitter:description', description);
            upsertMeta('name', 'twitter:image', image);

            upsertLink('canonical', canonical);
        });

        return () => { cancelled = true; };
    }, [location.pathname]);

    return null;
}
